import { lazy, Suspense, type ReactNode } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { LoadingPanel } from './components/ui/misc'
import { useCurrentUser } from './hooks/useCurrentUser'
import { ChangePassword, Profile } from './routes/account/Profile'
import { MyWorkspace } from './routes/workspace/MyWorkspace'
import { SharedWorkspaces } from './routes/workspaces/SharedWorkspaces'
import { Compare } from './routes/compare/Compare'
import { ForgotPassword, ResetPassword } from './routes/auth/PasswordReset'
import { SignIn } from './routes/auth/SignIn'
import { SignUp } from './routes/auth/SignUp'
import { RegistrationSubmitted, VerifyEmail } from './routes/auth/VerifyEmail'
import { Admin } from './routes/admin/Admin'
import { Database } from './routes/database/Database'
import { RecordEditor } from './routes/database/RecordEditor'

// Plotly is the single largest dependency and only this route needs it.
const ColourSpace = lazy(() =>
  import('./routes/colour-space/ColourSpace').then((module) => ({ default: module.ColourSpace })),
)

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<AnonymousOnly><SignIn /></AnonymousOnly>} />
      <Route path="/sign-up" element={<AnonymousOnly><SignUp /></AnonymousOnly>} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/registration-submitted" element={<RegistrationSubmitted />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      <Route element={<Protected><AppShell /></Protected>}>
        <Route index element={<Navigate to="/database" replace />} />
        <Route path="/database/new" element={<RecordEditor />} />
        <Route path="/database/:recordId/edit" element={<RecordEditor />} />
        <Route path="/database/*" element={<Database />} />
        <Route path="/workspace/*" element={<MyWorkspace />} />
        <Route path="/projects/*" element={<SharedWorkspaces />} />
        <Route path="/workspaces/*" element={<LegacyProjectsRedirect />} />
        <Route path="/compare" element={<Compare />} />
        <Route
          path="/colour-space"
          element={
            <Suspense fallback={<LoadingPanel label="Loading colour space" />}>
              <ColourSpace />
            </Suspense>
          }
        />
        <Route path="/admin/*" element={<AdminOnly><Admin /></AdminOnly>} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/drafts" element={<Navigate to="/workspace/drafts" replace />} />
      </Route>

      <Route path="*" element={<Navigate to="/database" replace />} />
    </Routes>
  )
}

/**
 * Gates the application on an approved account. A signed-in but unapproved user is
 * sent to the awaiting-approval screen rather than the sign-in form, so they are
 * not left guessing why their credentials "failed".
 */
function Protected({ children }: { children: ReactNode }) {
  const { user, isLoading } = useCurrentUser()
  const location = useLocation()

  if (isLoading) return <LoadingPanel label="Checking your session" />

  if (!user) {
    return <Navigate to="/sign-in" replace state={{ from: location.pathname }} />
  }

  if (!user.emailVerified) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />
  }

  if (user.accountStatus !== 'active') {
    return <Navigate to={`/registration-submitted?email=${encodeURIComponent(user.email)}`} replace />
  }

  return <>{children}</>
}

function AnonymousOnly({ children }: { children: ReactNode }) {
  const { user, isLoading, isActive } = useCurrentUser()
  if (isLoading) return <LoadingPanel />
  if (user && isActive) return <Navigate to="/database" replace />
  return <>{children}</>
}

/** Invitation emails sent before the rename still link to /workspaces/join. */
function LegacyProjectsRedirect() {
  const location = useLocation()
  const target = location.pathname.replace(/^\/workspaces/, '/projects') + location.search
  return <Navigate to={target} replace />
}

function AdminOnly({ children }: { children: ReactNode }) {
  const { isAdmin, isLoading } = useCurrentUser()
  if (isLoading) return <LoadingPanel />
  if (!isAdmin) return <Navigate to="/database" replace />
  return <>{children}</>
}
